import { ConnectionType, DataFlow, IntegrationType } from '../catalog/enums';
import { IntegrationIDs } from '../catalog/ids';
import type { IntegrationFilters, IntegrationGroup, SynthesizedIntegration } from './types';

/** Backend `type` strings that classify as `Direct` when no connectionType is set. */
const DIRECT_TYPES: readonly IntegrationType[] = [
  IntegrationType.experiment,
  IntegrationType.native,
  IntegrationType.nativeDataOut,
  IntegrationType.connectPartner,
  IntegrationType.existing,
  IntegrationType.partnerBuilt,
  IntegrationType.pipelinesSource,
  IntegrationType.pipelinesDestination,
];

/* ------------------------------------------------------------------------- */
/* Filtering                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Search a `name|category|searchableType` string and apply the category / type
 * (data-flow) / connection-type filters. `publicType` `Connect-partner`
 * normalizes to `Connect Partner` in the searchable string.
 */
export function filterIntegrations(
  integrations: readonly SynthesizedIntegration[],
  filters: IntegrationFilters,
): SynthesizedIntegration[] {
  const { query, selectedCategory, selectedType, selectedConnectionType } = filters;

  return integrations.filter(({ name, category, publicType, dataFlow, connectionType }) => {
    let searchableType: string = publicType;
    switch (publicType) {
      case IntegrationType.connectPartner: {
        searchableType = 'Connect Partner';
        break;
      }
      case IntegrationType.existing: {
        searchableType = 'Existing';
        break;
      }
    }
    const searchable = `${name}|${category}|${searchableType}`.toLowerCase();

    const matchType = () => {
      if (selectedType === '') {
        return true;
      }
      const filterType = selectedType.toLowerCase();
      if (filterType === 'data in') {
        return dataFlow === DataFlow.in;
      }
      if (filterType === 'data out') {
        return dataFlow === DataFlow.out;
      }
      if (filterType === 'channel') {
        return dataFlow === DataFlow.channel;
      }
      return publicType.toLowerCase() === filterType;
    };

    const matchConnectionType = () => {
      if (selectedConnectionType === '') {
        return true;
      }
      return connectionType === selectedConnectionType;
    };

    return (
      searchable.includes(query.toLowerCase()) &&
      (selectedCategory === '' || (category !== undefined && category.includes(selectedCategory))) &&
      matchType() &&
      matchConnectionType()
    );
  });
}

/* ------------------------------------------------------------------------- */
/* SQL-import directory gate                                                  */
/* ------------------------------------------------------------------------- */

const SQL_IMPORT_INTEGRATIONS = new Set<string>([
  IntegrationIDs.mysql,
  IntegrationIDs.postgres,
  IntegrationIDs.snowflakeIn,
  IntegrationIDs.bigQueryIn,
  IntegrationIDs.redshiftIn,
  IntegrationIDs.sqlserver,
]);

/** Keep an integration unless it is an unused SQL-import (used ones always show). */
export function filterSQLImportUnlessInUse(integration: Pick<SynthesizedIntegration, 'id' | 'enabled'>): boolean {
  return integration.enabled === true || !SQL_IMPORT_INTEGRATIONS.has(integration.id);
}

/**
 * The directory's filtered list: the base filter, then — unless the
 * `cdp_journeys_sql_import_available` flag is on — hide SQL-import integrations
 * that aren't already in use.
 */
export function directoryFilteredIntegrations(
  visibleIntegrations: readonly SynthesizedIntegration[],
  filters: IntegrationFilters,
  sqlImportAvailable: boolean,
): SynthesizedIntegration[] {
  const filtered = filterIntegrations(visibleIntegrations, filters);
  if (sqlImportAvailable) {
    return filtered;
  }
  return filtered.filter(filterSQLImportUnlessInUse);
}

/* ------------------------------------------------------------------------- */
/* Sorting                                                                    */
/* ------------------------------------------------------------------------- */

/** Compare a possibly-undefined number: undefined sorts first, then ascending. */
function compareForceOrder(a: number | undefined, b: number | undefined): number {
  const aUndefined = a === undefined;
  const bUndefined = b === undefined;
  if (aUndefined && bUndefined) return 0;
  if (aUndefined) return -1;
  if (bUndefined) return 1;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** Compare strings with plain `<`/`>` (uppercase sorts before lowercase, no locale collation). */
function compareName(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** Sort ascending by `['forceOrder', 'name']`. Stable. */
export function sortIntegrations(integrations: readonly SynthesizedIntegration[]): SynthesizedIntegration[] {
  return [...integrations].sort((a, b) => {
    const byForceOrder = compareForceOrder(a.forceOrder, b.forceOrder);
    if (byForceOrder !== 0) {
      return byForceOrder;
    }
    return compareName(a.name, b.name);
  });
}

/* ------------------------------------------------------------------------- */
/* Visible-integrations overrides                                             */
/* ------------------------------------------------------------------------- */

/** The index-tab visible set — hide the CDP in/out cards. */
export function visibleIntegrationsIndex(integrations: readonly SynthesizedIntegration[]): SynthesizedIntegration[] {
  return integrations.filter(({ id }) => id !== IntegrationIDs.cdpOut && id !== IntegrationIDs.cdpIn);
}

/**
 * The directory-tab visible set — also hide the Track API card and any
 * non-existent (spreadsheet placeholder) integrations.
 */
export function visibleIntegrationsDirectory(
  integrations: readonly SynthesizedIntegration[],
): SynthesizedIntegration[] {
  return visibleIntegrationsIndex(integrations).filter(
    ({ id, type }) => !(id === IntegrationIDs.customerioAPI || type === IntegrationType.nonExistent),
  );
}

/* ------------------------------------------------------------------------- */
/* Categories                                                                 */
/* ------------------------------------------------------------------------- */

/** Unique non-empty category values, alphabetically sorted. */
export function categories(integrations: readonly SynthesizedIntegration[]): string[] {
  const seen = new Set<string>();
  for (const { category } of integrations) {
    if (category != null && category !== '') {
      seen.add(category);
    }
  }
  return [...seen].sort(compareName);
}

/** `categorieOptions` — the category select options with a leading "All categories". */
export function categoryOptions(integrations: readonly SynthesizedIntegration[]): { label: string; value: string }[] {
  return [
    { label: 'All categories', value: '' },
    ...categories(integrations).map((category) => ({ label: category, value: category })),
  ];
}

/* ------------------------------------------------------------------------- */
/* Grouping                                                                   */
/* ------------------------------------------------------------------------- */

/** `isDirectIntegration` — explicit `Direct` connectionType, else a type fallback. */
export function isDirectIntegration(integration: Pick<SynthesizedIntegration, 'connectionType' | 'type'>): boolean {
  if (integration.connectionType != null) {
    return integration.connectionType === ConnectionType.direct;
  }
  return DIRECT_TYPES.includes(integration.type);
}

/** `isZapierIntegration` — Zapier type, or Zapier connectionType on a non-placeholder. */
export function isZapierIntegration(integration: Pick<SynthesizedIntegration, 'connectionType' | 'type'>): boolean {
  return (
    (integration.type !== IntegrationType.nonExistent && integration.connectionType === ConnectionType.zapier) ||
    integration.type === IntegrationType.zapier
  );
}

/** The directory ordered group keys — Zapier is computed but not in this list. */
export const DIRECTORY_GROUP_ORDER = [ConnectionType.direct, ConnectionType.developer, ConnectionType.webhook] as const;

/**
 * Group the sorted+filtered list. Direct / Developer / Webhook draw from it;
 * Zapier is computed separately (rendered outside the main groups).
 */
export function groupIntegrations(
  sortedIntegrations: readonly SynthesizedIntegration[],
): Record<ConnectionType, IntegrationGroup> {
  const byConnectionType = (t: ConnectionType) => (i: SynthesizedIntegration) => i.connectionType === t;
  return {
    [ConnectionType.direct]: {
      label: 'Direct integrations',
      tooltip: 'These tools have a pre-built, supported way to seamlessly connect to Customer.io.',
      integrations: sortedIntegrations.filter(isDirectIntegration),
    },
    [ConnectionType.developer]: {
      label: 'Developer-enabled',
      tooltip: 'These install Customer.io into your app, or they connect a tool using code.',
      integrations: sortedIntegrations.filter(byConnectionType(ConnectionType.developer)),
    },
    [ConnectionType.webhook]: {
      label: 'Webhook-enabled',
      tooltip: 'These tools are connected using webhooks that you configure.',
      integrations: sortedIntegrations.filter(byConnectionType(ConnectionType.webhook)),
    },
    [ConnectionType.zapier]: {
      label: '',
      tooltip: '',
      integrations: sortedIntegrations.filter(isZapierIntegration),
    },
  };
}
